import React, { useEffect, useState } from 'react';
import {
  Modal, Form, Input, Select, DatePicker, TimePicker,
  Switch, Row, Col, message,
} from 'antd';
import dayjs from 'dayjs';
import * as adminactions from 'store/actions/AuthActions';

const { TextArea } = Input;
const { Option } = Select;

const EventFormModal = ({ visible, onCancel, onSuccess, editingRecord, employees, departments }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const visibilityType = Form.useWatch('visibility_type', form);

  useEffect(() => {
    if (visible) {
      if (editingRecord) {
        form.setFieldsValue({
          ...editingRecord,
          event_date: editingRecord.event_date ? dayjs(editingRecord.event_date) : null,
          start_time: editingRecord.start_time ? dayjs(editingRecord.start_time, 'HH:mm:ss') : null,
          end_time: editingRecord.end_time ? dayjs(editingRecord.end_time, 'HH:mm:ss') : null,
          is_all_day: editingRecord.is_all_day || false,
          departments: editingRecord.departments || [],
          visible_to_employee_ids: editingRecord.visible_to_employees
            ? editingRecord.visible_to_employees.map(e => typeof e === 'object' ? e.id : e)
            : [],
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          event_type: 'event',
          visibility_type: 'all',
          is_all_day: false,
        });
      }
    }
  }, [visible, editingRecord, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const data = {
        title: values.title,
        description: values.description || '',
        event_date: values.event_date?.format('YYYY-MM-DD'),
        start_time: values.is_all_day ? null : values.start_time?.format('HH:mm:ss'),
        end_time: values.is_all_day ? null : values.end_time?.format('HH:mm:ss'),
        event_type: values.event_type,
        visibility_type: values.visibility_type,
        departments: values.visibility_type === 'department' ? (values.departments || []) : [],
        visible_to_employee_ids: values.visibility_type === 'employee' ? (values.visible_to_employee_ids || []) : [],
        location: values.location || '',
        is_all_day: values.is_all_day || false,
      };

      if (editingRecord) {
        await adminactions.updateEvent(data, editingRecord.id);
        message.success('Event updated successfully');
      } else {
        await adminactions.createEvent(data);
        message.success('Event created successfully');
      }

      onSuccess();
      form.resetFields();
    } catch (error) {
      message.error('Failed to save event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={editingRecord ? 'Edit Event' : 'Create Event'}
      open={visible}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={700}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Row gutter={16}>
          <Col span={16}>
            <Form.Item
              name="title"
              label="Title"
              rules={[{ required: true, message: 'Please enter a title' }]}
            >
              <Input placeholder="Event title" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="event_type"
              label="Type"
              rules={[{ required: true }]}
            >
              <Select>
                <Option value="event">Event</Option>
                <Option value="meeting">Meeting</Option>
                <Option value="reminder">Reminder</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="description" label="Description">
          <TextArea rows={3} placeholder="Event description (optional)" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="event_date"
              label="Date"
              rules={[{ required: true, message: 'Please select a date' }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="is_all_day" label="All Day" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="start_time" label="Start Time">
              <TimePicker
                style={{ width: '100%' }}
                format="HH:mm"
                disabled={form.getFieldValue('is_all_day')}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="end_time" label="End Time">
              <TimePicker
                style={{ width: '100%' }}
                format="HH:mm"
                disabled={form.getFieldValue('is_all_day')}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="location" label="Location">
              <Input placeholder="Location (optional)" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="visibility_type"
              label="Visibility"
              rules={[{ required: true }]}
            >
              <Select>
                <Option value="all">All Employees</Option>
                <Option value="department">Specific Departments</Option>
                <Option value="employee">Specific Employees</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {visibilityType === 'department' && (
          <Form.Item
            name="departments"
            label="Departments"
            rules={[{ required: true, message: 'Please select at least one department' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select departments"
              options={departments.map(d => ({ label: d.name, value: d.name }))}
            />
          </Form.Item>
        )}

        {visibilityType === 'employee' && (
          <Form.Item
            name="visible_to_employee_ids"
            label="Employees"
            rules={[{ required: true, message: 'Please select at least one employee' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select employees"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={employees.map(emp => ({
                label: `${emp.user?.first_name || ''} ${emp.user?.last_name || ''}`.trim() || emp.employee_id,
                value: emp.id,
              }))}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default EventFormModal;
